// Angular
import {
  Component,
  OnInit,
  signal,
  ViewChild,
  inject,
  Output,
  EventEmitter,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';
// PrimeNG
import { SkeletonModule } from 'primeng/skeleton';
// Services
import { SessionService } from '@core/services-v2/session/session.service';
import { CustomerAddressApiService } from '@core/services-v2/customer-address/customer-address-api.service';
import { getAddressOptions } from './utils/address.utils';
// Models
import { ISession } from '@core/models-v2/auth/session.interface';
import { AddressOption } from './models/address-option.type';
// Components
import { AddressListSidebarComponent } from '../address-list-sidebar/address-list-sidebar.component';

const NG_MODULES = [ReactiveFormsModule, NgClass];
const PRIME_MODULES = [SkeletonModule];
const COMPONENTS = [AddressListSidebarComponent];

@Component({
  selector: 'app-delivery-address-form',
  standalone: true,
  imports: [NG_MODULES, PRIME_MODULES, COMPONENTS],
  templateUrl: './delivery-address-form.component.html',
})
export class DeliveryAddressFormComponent implements OnInit {
  //#region ViewChild components
  @ViewChild('sidebar') sidebar!: AddressListSidebarComponent;
  //#endregion

  //#region Output events
  @Output() onChange: EventEmitter<AddressOption | null> = new EventEmitter();
  //#endregion

  addresses = signal<AddressOption[]>([]);
  selectedAddress = signal<AddressOption | null>(null);
  isInvalidSelectedAddressInput = signal<boolean>(false);
  areLoadingAddresses = signal<boolean>(true);
  session!: ISession;

  //#region Dependencies
  public readonly sessionService = inject(SessionService);
  public readonly customerAddressApiService = inject(
    CustomerAddressApiService
  );
  //#endregion

  ngOnInit(): void {
    this.session = this.sessionService.getSession();

    if (this.sessionService.isLoggedIn()) {
      this.getCustomerAddresses();
    } else {
      this.onChange.emit(null);
      this.areLoadingAddresses.set(false);
    }
  }

  /**
   * Al seleccionar una dirección de despacho desde el sidebar.
   * @param selectedAddress - Dirección seleccionada.
   */
  onSelectAddress(selectedAddress: AddressOption | null): void {
    this.selectedAddress.set(selectedAddress);
    if (selectedAddress) this.isInvalidSelectedAddressInput.set(false);
    this.onChange.emit(selectedAddress);
  }

  /**
   * Obtener las direcciones de despacho del cliente.
   */
  private getCustomerAddresses(): void {
    this.areLoadingAddresses.set(true);
    const { documentId } = this.session;
    this.customerAddressApiService.getDeliveryAddresses(documentId).subscribe({
      next: (addresses) => {
        if (addresses.length) {
          //Start::obtener última dirección (id más alto).
          // - FIXME:se debería buscar la forma de obtener la dirección utilizada la última vez.
          const addressesIds = addresses.map((address) => Number(address.id));
          const selectedShippingId = String(Math.max(...addressesIds));
          //End::obtener última dirección (id más alto).
          const addressOptions = getAddressOptions(
            addresses,
            selectedShippingId
          );
          const selectedAddress = addressOptions.find(
            (address) => address.isDefault
          );
          if (selectedAddress) {
            this.selectedAddress.set(selectedAddress);
            this.isInvalidSelectedAddressInput.set(false);
          }
          this.addresses.set(addressOptions);
        }
        this.onChange.emit(this.selectedAddress());
        this.areLoadingAddresses.set(false);
      },
      error: () => {
        this.areLoadingAddresses.set(false);
      },
    });
  }

  /**
   * Dispara el evento submit del formulario y valida su estado.
   * - Este método es llamado desde el componente padre.
   */
  submit(): boolean {
    if (this.selectedAddress()) return true;
    this.isInvalidSelectedAddressInput.set(true);
    return false;
  }
}
